import { Component, inject, OnInit, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { InstitutionService } from '../../core/services/institution.service';
import { OnboardingService } from '../../core/services/onboarding.service';
import { ToastService } from '../../core/services/toast.service';
import { AuthService } from '../../core/services/auth.service';
import { InstitutionResponse, UpdateInstitutionRequest } from '../../core/models/models';
import { StatusBadgeComponent } from '../../shared/components/status-badge/status-badge.component';
import { ModalComponent, ModalErrorState } from '../../shared/components/modal/modal.component';
import { extractErrorDetails } from '../../core/interceptors/response.interceptor';
import { PhoneInputComponent } from '../../shared/components/phone-input/phone-input.component';

export interface ParsedDocument {
  content: string;
  fileType: string;
  dataUrl: string;
  fileName: string;
  isPdf: boolean;
  isImage: boolean;
}

@Component({
  selector: 'app-institution-list',
  standalone: true,
  imports: [CommonModule, FormsModule, StatusBadgeComponent, ModalComponent, PhoneInputComponent],
  template: `
    <div class="space-y-6 font-sans">
      <!-- Header -->
      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 class="text-2xl font-extrabold text-slate-900 tracking-tight flex items-center gap-2.5">
            <div class="w-8 h-8 rounded-xl bg-gradient-to-tr from-emerald-600 to-teal-500 flex items-center justify-center text-white shadow-sm">
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            Institution Directory & Governance
          </h1>
          <p class="text-xs text-slate-500 mt-1">
            Monitor, inspect, activate, and manage all onboarded government ministries, departments, and partner institutions.
          </p>
        </div>

        <div class="flex items-center gap-2">
          <button
            (click)="loadInstitutions()"
            [disabled]="loading()"
            class="inline-flex items-center gap-2 py-2 px-3.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 font-bold text-xs shadow-2xs transition active:scale-95"
          >
            <svg class="w-3.5 h-3.5 text-emerald-600" [class.animate-spin]="loading()" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      <!-- KPI Metrics Cards -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <!-- Total Institutions -->
        <div class="glass-panel p-4 rounded-2xl border border-slate-200 bg-white shadow-xs">
          <div class="flex items-center justify-between text-slate-500 text-xs font-semibold">
            <span>Total Institutions</span>
            <span class="p-1.5 rounded-lg bg-emerald-50 text-emerald-700">
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5" />
              </svg>
            </span>
          </div>
          <div class="mt-2 text-2xl font-black text-slate-900">{{ institutions().length }}</div>
          <div class="text-[11px] text-slate-500 mt-0.5">Total onboarded ecosystem members</div>
        </div>

        <!-- Active Institutions -->
        <div class="glass-panel p-4 rounded-2xl border border-slate-200 bg-white shadow-xs">
          <div class="flex items-center justify-between text-slate-500 text-xs font-semibold">
            <span>Active Status</span>
            <span class="p-1.5 rounded-lg bg-emerald-50 text-emerald-700">
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </span>
          </div>
          <div class="mt-2 text-2xl font-black text-emerald-600">{{ activeCount() }}</div>
          <div class="text-[11px] text-slate-500 mt-0.5">Currently enabled & active</div>
        </div>

        <!-- Security Server Connected -->
        <div class="glass-panel p-4 rounded-2xl border border-slate-200 bg-white shadow-xs">
          <div class="flex items-center justify-between text-slate-500 text-xs font-semibold">
            <span>Security Server (SS)</span>
            <span class="p-1.5 rounded-lg bg-sky-50 text-sky-700">
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
              </svg>
            </span>
          </div>
          <div class="mt-2 text-2xl font-black text-sky-700">{{ securityServerCount() }}</div>
          <div class="text-[11px] text-slate-500 mt-0.5">Zan-x-Change Security Server configured</div>
        </div>

        <!-- Inactive / Pending -->
        <div class="glass-panel p-4 rounded-2xl border border-slate-200 bg-white shadow-xs">
          <div class="flex items-center justify-between text-slate-500 text-xs font-semibold">
            <span>Deactivated</span>
            <span class="p-1.5 rounded-lg bg-rose-50 text-rose-700">
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
            </span>
          </div>
          <div class="mt-2 text-2xl font-black text-rose-600">{{ inactiveCount() }}</div>
          <div class="text-[11px] text-slate-500 mt-0.5">Disabled or restricted</div>
        </div>
      </div>

      <!-- Search & Filters Toolbar -->
      <div class="glass-panel p-4 rounded-2xl border border-slate-200 bg-white flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4 shadow-sm">
        <div class="flex items-center gap-3 flex-1">
          <div class="relative w-full max-w-sm">
            <input
              type="text"
              [(ngModel)]="searchQuery"
              placeholder="Search by name, email, vote, or reg number..."
              class="w-full pl-9 pr-4 py-2 rounded-xl bg-white border border-slate-300 text-xs text-slate-900 placeholder-slate-400 focus:border-emerald-500"
            />
            <svg class="w-4 h-4 text-slate-400 absolute left-3 top-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>

          <!-- Status Filter Tabs -->
          <div class="hidden sm:flex items-center gap-1 p-1 bg-slate-100 rounded-xl border border-slate-200 text-xs font-bold">
            <button
              (click)="selectedStatusFilter.set('ALL')"
              class="px-3 py-1 rounded-lg transition cursor-pointer"
              [class.bg-white]="selectedStatusFilter() === 'ALL'"
              [class.shadow-2xs]="selectedStatusFilter() === 'ALL'"
              [class.text-emerald-700]="selectedStatusFilter() === 'ALL'"
              [class.text-slate-600]="selectedStatusFilter() !== 'ALL'"
            >
              All
            </button>
            <button
              (click)="selectedStatusFilter.set('ACTIVE')"
              class="px-3 py-1 rounded-lg transition cursor-pointer"
              [class.bg-white]="selectedStatusFilter() === 'ACTIVE'"
              [class.shadow-2xs]="selectedStatusFilter() === 'ACTIVE'"
              [class.text-emerald-700]="selectedStatusFilter() === 'ACTIVE'"
              [class.text-slate-600]="selectedStatusFilter() !== 'ACTIVE'"
            >
              Active
            </button>
            <button
              (click)="selectedStatusFilter.set('INACTIVE')"
              class="px-3 py-1 rounded-lg transition cursor-pointer"
              [class.bg-white]="selectedStatusFilter() === 'INACTIVE'"
              [class.shadow-2xs]="selectedStatusFilter() === 'INACTIVE'"
              [class.text-emerald-700]="selectedStatusFilter() === 'INACTIVE'"
              [class.text-slate-600]="selectedStatusFilter() !== 'INACTIVE'"
            >
              Inactive
            </button>
          </div>

          <!-- Regulatory Decision Filter Tabs -->
          @if (authService.hasRole('SuperAdmin') || authService.isInternal()) {
            <div class="hidden lg:flex items-center gap-1 p-1 bg-slate-100 rounded-xl border border-slate-200 text-xs font-bold">
              <button
                (click)="selectedApprovalFilter.set('ALL')"
                class="px-2.5 py-1 rounded-lg transition cursor-pointer"
                [class.bg-white]="selectedApprovalFilter() === 'ALL'"
                [class.shadow-2xs]="selectedApprovalFilter() === 'ALL'"
                [class.text-emerald-700]="selectedApprovalFilter() === 'ALL'"
                [class.text-slate-600]="selectedApprovalFilter() !== 'ALL'"
              >
                All Decisions
              </button>
              <button
                (click)="selectedApprovalFilter.set('PENDING')"
                class="px-2.5 py-1 rounded-lg transition cursor-pointer"
                [class.bg-white]="selectedApprovalFilter() === 'PENDING'"
                [class.shadow-2xs]="selectedApprovalFilter() === 'PENDING'"
                [class.text-amber-700]="selectedApprovalFilter() === 'PENDING'"
                [class.text-slate-600]="selectedApprovalFilter() !== 'PENDING'"
              >
                Pending Review
              </button>
              <button
                (click)="selectedApprovalFilter.set('CORRECTION')"
                class="px-2.5 py-1 rounded-lg transition cursor-pointer"
                [class.bg-white]="selectedApprovalFilter() === 'CORRECTION'"
                [class.shadow-2xs]="selectedApprovalFilter() === 'CORRECTION'"
                [class.text-amber-700]="selectedApprovalFilter() === 'CORRECTION'"
                [class.text-slate-600]="selectedApprovalFilter() !== 'CORRECTION'"
              >
                Returned
              </button>
              <button
                (click)="selectedApprovalFilter.set('APPROVED')"
                class="px-2.5 py-1 rounded-lg transition cursor-pointer"
                [class.bg-white]="selectedApprovalFilter() === 'APPROVED'"
                [class.shadow-2xs]="selectedApprovalFilter() === 'APPROVED'"
                [class.text-emerald-700]="selectedApprovalFilter() === 'APPROVED'"
                [class.text-slate-600]="selectedApprovalFilter() !== 'APPROVED'"
              >
                Approved
              </button>
              <button
                (click)="selectedApprovalFilter.set('REJECTED')"
                class="px-2.5 py-1 rounded-lg transition cursor-pointer"
                [class.bg-white]="selectedApprovalFilter() === 'REJECTED'"
                [class.shadow-2xs]="selectedApprovalFilter() === 'REJECTED'"
                [class.text-rose-700]="selectedApprovalFilter() === 'REJECTED'"
                [class.text-slate-600]="selectedApprovalFilter() !== 'REJECTED'"
              >
                Rejected
              </button>
            </div>
          }
        </div>

        <div class="text-xs text-slate-500 font-mono font-semibold self-center sm:self-auto">
          Showing <span class="text-emerald-700 font-bold">{{ filteredInstitutions().length }}</span> of {{ institutions().length }} institutions
        </div>
      </div>

      <!-- Institutions Table View -->
      <div class="glass-panel rounded-3xl border border-slate-200 bg-white overflow-hidden shadow-sm">
        <div class="overflow-x-auto">
          <table class="w-full text-left border-collapse text-xs">
            <thead>
              <tr class="bg-slate-100/80 text-slate-600 uppercase tracking-wider font-bold border-b border-slate-200">
                <th class="p-4">Institution Name</th>
                <th class="p-4">Vote / Reg No</th>
                <th class="p-4">Official Contact</th>
                <th class="p-4">DPO Contact</th>
                <th class="p-4">Security Server</th>
                <th class="p-4">Approval</th>
                <th class="p-4">Active State</th>
                <th class="p-4">Onboarded</th>
                <th class="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-200/80">
              @if (loading() && institutions().length === 0) {
                <tr>
                  <td colspan="9" class="p-8 text-center text-slate-400 font-medium">
                    <div class="flex items-center justify-center gap-2">
                      <svg class="w-5 h-5 animate-spin text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Loading onboarded institutions...
                    </div>
                  </td>
                </tr>
              } @else if (filteredInstitutions().length === 0) {
                <tr>
                  <td colspan="9" class="p-8 text-center text-slate-400 font-medium">
                    No institutions found matching the current search criteria.
                  </td>
                </tr>
              } @else {
                @for (inst of filteredInstitutions(); track inst.uid) {
                  <tr class="hover:bg-emerald-50/25 transition-colors">
                    <!-- Name & Avatar -->
                    <td class="p-4">
                      <div class="flex items-center gap-3">
                        <div class="w-8 h-8 rounded-lg bg-emerald-100 border border-emerald-300 text-emerald-800 font-extrabold flex items-center justify-center text-xs shadow-2xs shrink-0">
                          {{ getInitials(inst.name) }}
                        </div>
                        <div>
                          <div class="flex items-center gap-1.5 flex-wrap">
                            <span class="font-bold text-slate-900 text-xs">{{ inst.name }}</span>
                            <span class="px-1.5 py-0.5 rounded text-[10px] font-extrabold uppercase tracking-wider bg-slate-100 text-slate-700 border border-slate-200">
                              {{ inst.memberClassCode || 'GOV' }}
                            </span>
                          </div>
                          <div class="text-[10px] text-slate-400 font-mono">UID: {{ inst.uid.substring(0, 8) }}...</div>
                        </div>
                      </div>
                    </td>

                    <!-- Vote / Reg No -->
                    <td class="p-4 font-mono">
                      @if (inst.voteNumber) {
                        <div class="font-bold text-slate-800">Vote: {{ inst.voteNumber }}</div>
                      }
                      <div class="text-[11px] text-slate-500">
                        {{ inst.registrationNumber ? 'Reg: ' + inst.registrationNumber : 'No Reg No' }}
                      </div>
                    </td>

                    <!-- Contact -->
                    <td class="p-4">
                      <div class="font-semibold text-emerald-700 font-mono text-[11px]">{{ inst.email }}</div>
                      <div class="text-[11px] text-slate-500 font-mono">{{ inst.phoneNumber }}</div>
                    </td>

                    <!-- DPO -->
                    <td class="p-4">
                      @if (inst.dpoName) {
                        <div class="font-bold text-slate-800">{{ inst.dpoName }}</div>
                        <div class="text-[10px] text-slate-500 font-mono">{{ inst.dpoEmail || inst.dpoPhone }}</div>
                      } @else {
                        <span class="text-slate-400 italic text-[11px]">Not assigned</span>
                      }
                    </td>

                    <!-- Security Server -->
                    <td class="p-4">
                      @if (inst.hasSs) {
                        <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                          <span class="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                          Connected
                        </span>
                      } @else {
                        <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-600 border border-slate-200">
                          <span class="w-1.5 h-1.5 rounded-full bg-slate-400"></span>
                          Pending SS
                        </span>
                      }
                    </td>

                    <!-- Approval Status -->
                    <td class="p-4">
                      <app-status-badge [status]="inst.approvalStatus"></app-status-badge>
                    </td>

                    <!-- Active Status Toggle Button -->
                    <td class="p-4">
                      <button
                        (click)="toggleActiveStatus(inst)"
                        [disabled]="togglingUid() === inst.uid"
                        [title]="inst.isActive ? 'Click to deactivate institution' : 'Click to activate institution'"
                        class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wide border transition-all cursor-pointer"
                        [class.bg-emerald-50]="inst.isActive"
                        [class.text-emerald-800]="inst.isActive"
                        [class.border-emerald-300]="inst.isActive"
                        [class.hover:bg-rose-50]="inst.isActive"
                        [class.hover:text-rose-700]="inst.isActive"
                        [class.hover:border-rose-300]="inst.isActive"
                        [class.bg-rose-50]="!inst.isActive"
                        [class.text-rose-700]="!inst.isActive"
                        [class.border-rose-300]="!inst.isActive"
                        [class.hover:bg-emerald-50]="!inst.isActive"
                        [class.hover:text-emerald-800]="!inst.isActive"
                        [class.hover:border-emerald-300]="!inst.isActive"
                      >
                        <span
                          class="w-2 h-2 rounded-full transition-colors"
                          [class.bg-emerald-500]="inst.isActive"
                          [class.bg-rose-500]="!inst.isActive"
                        ></span>
                        {{ inst.isActive ? 'Active' : 'Inactive' }}
                      </button>
                    </td>

                    <!-- Created At -->
                    <td class="p-4 font-mono text-slate-500 text-[11px]">
                      {{ inst.createdAt | date:'mediumDate' }}
                    </td>

                    <!-- Actions -->
                    <td class="p-4 text-right">
                      <div class="inline-flex items-center gap-1.5">
                        <!-- View Details & Preview Documents -->
                        <button
                          (click)="openViewModal(inst)"
                          title="View Details & Preview Letter"
                          class="p-1.5 rounded-lg bg-slate-100 hover:bg-emerald-50 hover:text-emerald-700 text-slate-600 transition border border-slate-200 cursor-pointer"
                        >
                          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>

                        @if (authService.canManageOnboarding()) {
                          <!-- Return for Correction Quick Action -->
                          <button
                            (click)="openDecisionModal(inst.uid, 'correction')"
                            title="Return Application for Correction"
                            class="p-1.5 rounded-lg bg-amber-50 hover:bg-amber-100 text-amber-700 transition border border-amber-200 cursor-pointer"
                          >
                            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                          </button>

                          <!-- Approve Quick Action -->
                          @if (inst.approvalStatus !== 'APPROVED') {
                            <button
                              (click)="onApproveInstitution(inst)"
                              title="Approve Institution Onboarding"
                              class="p-1.5 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 transition border border-emerald-200 cursor-pointer"
                            >
                              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                              </svg>
                            </button>
                          }

                          <!-- Reject Quick Action -->
                          @if (inst.approvalStatus !== 'REJECTED') {
                            <button
                              (click)="openDecisionModal(inst.uid, 'reject')"
                              title="Reject Institution Onboarding"
                              class="p-1.5 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-700 transition border border-rose-200 cursor-pointer"
                            >
                              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          }
                        }

                        <!-- Edit Details -->
                        <button
                          (click)="openEditModal(inst)"
                          title="Edit Details"
                          class="p-1.5 rounded-lg bg-slate-100 hover:bg-emerald-50 hover:text-emerald-700 text-slate-600 transition border border-slate-200 cursor-pointer"
                        >
                          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                }
              }
            </tbody>
          </table>
        </div>
      </div>

      <!-- VIEW INSTITUTION DETAILS MODAL -->
      <app-modal
        [isOpen]="showViewModal()"
        title="Institution Governance & Compliance Details"
        maxWidth="max-w-4xl"
        (close)="showViewModal.set(false)"
      >
        @if (selectedInstitution(); as inst) {
          <div class="space-y-5 text-xs text-slate-700">
            <!-- Header Card -->
            <div class="flex items-center gap-3 p-3.5 rounded-2xl bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200">
              <div class="w-10 h-10 rounded-xl bg-emerald-600 text-white font-extrabold flex items-center justify-center text-sm shadow-sm">
                {{ getInitials(inst.name) }}
              </div>
              <div class="flex-1">
                <h3 class="text-sm font-bold text-slate-900">{{ inst.name }}</h3>
                <div class="text-[11px] text-slate-500 font-mono">UID: {{ inst.uid }} | Sector: {{ inst.memberClassName || inst.memberClassCode || 'GOV' }}</div>
              </div>
              <div class="flex items-center gap-2">
                <app-status-badge [status]="inst.approvalStatus"></app-status-badge>
              </div>
            </div>

            <!-- Detail Grid -->
            <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div class="p-3 rounded-xl bg-slate-50 border border-slate-200">
                <span class="text-[10px] font-bold uppercase text-slate-400 block mb-0.5">Official Email</span>
                <span class="font-mono font-bold text-slate-900 truncate block" [title]="inst.email">{{ inst.email }}</span>
              </div>
              <div class="p-3 rounded-xl bg-slate-50 border border-slate-200">
                <span class="text-[10px] font-bold uppercase text-slate-400 block mb-0.5">Official Phone</span>
                <span class="font-mono font-bold text-slate-900">{{ inst.phoneNumber }}</span>
              </div>
              <div class="p-3 rounded-xl bg-slate-50 border border-slate-200">
                <span class="text-[10px] font-bold uppercase text-slate-400 block mb-0.5">Vote Number</span>
                <span class="font-mono font-bold text-slate-900">{{ inst.voteNumber || 'None' }}</span>
              </div>
              <div class="p-3 rounded-xl bg-slate-50 border border-slate-200">
                <span class="text-[10px] font-bold uppercase text-slate-400 block mb-0.5">Registration Number</span>
                <span class="font-mono font-bold text-slate-900">{{ inst.registrationNumber || 'None' }}</span>
              </div>
              <div class="p-3 rounded-xl bg-slate-50 border border-slate-200">
                <span class="text-[10px] font-bold uppercase text-slate-400 block mb-0.5">Security Server Status</span>
                <span class="font-bold" [class.text-emerald-600]="inst.hasSs" [class.text-slate-500]="!inst.hasSs">
                  {{ inst.hasSs ? 'Configured & Enabled' : 'Not Configured' }}
                </span>
              </div>
              <div class="p-3 rounded-xl bg-slate-50 border border-slate-200">
                <span class="text-[10px] font-bold uppercase text-slate-400 block mb-0.5">Active Account Status</span>
                <span class="font-bold" [class.text-emerald-600]="inst.isActive" [class.text-rose-600]="!inst.isActive">
                  {{ inst.isActive ? 'Active (Operational)' : 'Inactive (Deactivated)' }}
                </span>
              </div>
              <div class="p-3 rounded-xl bg-slate-50 border border-slate-200">
                <span class="text-[10px] font-bold uppercase text-slate-400 block mb-0.5">SLA Agreement</span>
                <span class="font-bold" [class.text-emerald-600]="inst.slaAccepted" [class.text-slate-500]="!inst.slaAccepted">
                  {{ inst.slaAccepted ? 'Signed & Compliant' : 'Pending Signing' }}
                </span>
              </div>
              <div class="p-3 rounded-xl bg-slate-50 border border-slate-200">
                <span class="text-[10px] font-bold uppercase text-slate-400 block mb-0.5">Approval Status</span>
                <span class="font-bold text-slate-800">{{ inst.approvalStatus }}</span>
              </div>
            </div>

            <!-- DPO Section -->
            <div class="p-3.5 rounded-2xl bg-slate-50 border border-slate-200 space-y-2">
              <div class="text-[11px] font-bold text-emerald-800 uppercase tracking-wider">
                Assigned Data Protection Officer (DPO)
              </div>
              <div class="grid grid-cols-3 gap-2">
                <div>
                  <span class="text-[10px] text-slate-400 block">DPO Name</span>
                  <span class="font-bold text-slate-900">{{ inst.dpoName || 'None' }}</span>
                </div>
                <div>
                  <span class="text-[10px] text-slate-400 block">DPO Email</span>
                  <span class="font-mono text-slate-800">{{ inst.dpoEmail || 'None' }}</span>
                </div>
                <div>
                  <span class="text-[10px] text-slate-400 block">DPO Phone</span>
                  <span class="font-mono text-slate-800">{{ inst.dpoPhone || 'None' }}</span>
                </div>
              </div>
            </div>

            <!-- Application Letter & Compliance Document Preview Section -->
            <div class="space-y-3 border-t border-slate-200 pt-4">
              <div class="flex items-center justify-between flex-wrap gap-2">
                <div class="flex items-center gap-2">
                  <button
                    (click)="activeDocTab.set('letter')"
                    class="px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer inline-flex items-center gap-1.5"
                    [ngClass]="activeDocTab() === 'letter' ? 'bg-emerald-600 text-white shadow-2xs' : 'bg-slate-100 text-slate-600 hover:text-slate-900'"
                  >
                    <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span>Application Letter</span>
                  </button>

                  @if (parsedCertificate()) {
                    <button
                      (click)="activeDocTab.set('certificate')"
                      class="px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer inline-flex items-center gap-1.5"
                      [ngClass]="activeDocTab() === 'certificate' ? 'bg-emerald-600 text-white shadow-2xs' : 'bg-slate-100 text-slate-600 hover:text-slate-900'"
                    >
                      <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                      </svg>
                      <span>Certificate</span>
                    </button>
                  }
                </div>

                <!-- Download button for active document -->
                @if (activeDocTab() === 'letter' && parsedDocument(); as doc) {
                  <button
                    (click)="downloadDocument(doc)"
                    class="px-3.5 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs inline-flex items-center gap-1.5 shadow-2xs transition cursor-pointer"
                  >
                    <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    <span>Download Application Letter</span>
                  </button>
                }
                @if (activeDocTab() === 'certificate' && parsedCertificate(); as cert) {
                  <button
                    (click)="downloadDocument(cert)"
                    class="px-3.5 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs inline-flex items-center gap-1.5 shadow-2xs transition cursor-pointer"
                  >
                    <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    <span>Download Certificate</span>
                  </button>
                }
              </div>

              <!-- Document View Area: Application Letter -->
              @if (activeDocTab() === 'letter') {
                @if (parsedDocument(); as doc) {
                  <div class="rounded-2xl border border-slate-200 overflow-hidden bg-slate-100 p-2 shadow-inner">
                    @if (doc.isPdf && sanitizedDocumentUrl()) {
                      <iframe
                        [src]="sanitizedDocumentUrl()"
                        class="w-full h-[520px] rounded-xl border border-slate-300 bg-white"
                        title="Application Letter PDF"
                      ></iframe>
                    } @else if (doc.isImage) {
                      <div class="max-h-[520px] overflow-auto flex items-center justify-center p-4 bg-white rounded-xl">
                        <img [src]="doc.dataUrl" [alt]="doc.fileName" class="max-h-[480px] max-w-full object-contain rounded-lg shadow-xs" />
                      </div>
                    } @else {
                      <div class="p-8 text-center bg-white rounded-xl space-y-3">
                        <div class="w-12 h-12 rounded-xl bg-emerald-100 text-emerald-800 mx-auto flex items-center justify-center font-extrabold uppercase">
                          {{ doc.fileType }}
                        </div>
                        <div>
                          <div class="font-bold text-sm text-slate-900">{{ doc.fileName }}</div>
                          <p class="text-xs text-slate-500 mt-1">This document format is best previewed by downloading directly.</p>
                        </div>
                        <button
                          (click)="downloadDocument(doc)"
                          class="px-5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs shadow-sm cursor-pointer"
                        >
                          Download {{ doc.fileName }}
                        </button>
                      </div>
                    }
                  </div>
                } @else {
                  <div class="p-8 rounded-2xl bg-slate-50 border border-slate-200 text-center text-slate-400">
                    <svg class="w-8 h-8 mx-auto text-slate-300 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span>No application letter attachment uploaded for this institution.</span>
                  </div>
                }
              }

              <!-- Document View Area: Certificate -->
              @if (activeDocTab() === 'certificate') {
                @if (parsedCertificate(); as cert) {
                  <div class="rounded-2xl border border-slate-200 overflow-hidden bg-slate-100 p-2 shadow-inner">
                    @if (cert.isPdf && sanitizedCertificateUrl()) {
                      <iframe
                        [src]="sanitizedCertificateUrl()"
                        class="w-full h-[520px] rounded-xl border border-slate-300 bg-white"
                        title="Compliance Certificate PDF"
                      ></iframe>
                    } @else if (cert.isImage) {
                      <div class="max-h-[520px] overflow-auto flex items-center justify-center p-4 bg-white rounded-xl">
                        <img [src]="cert.dataUrl" [alt]="cert.fileName" class="max-h-[480px] max-w-full object-contain rounded-lg shadow-xs" />
                      </div>
                    } @else {
                      <div class="p-8 text-center bg-white rounded-xl space-y-3">
                        <div class="w-12 h-12 rounded-xl bg-emerald-100 text-emerald-800 mx-auto flex items-center justify-center font-extrabold uppercase">
                          {{ cert.fileType }}
                        </div>
                        <div>
                          <div class="font-bold text-sm text-slate-900">{{ cert.fileName }}</div>
                          <p class="text-xs text-slate-500 mt-1">This document format is best previewed by downloading directly.</p>
                        </div>
                        <button
                          (click)="downloadDocument(cert)"
                          class="px-5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs shadow-sm cursor-pointer"
                        >
                          Download {{ cert.fileName }}
                        </button>
                      </div>
                    }
                  </div>
                } @else {
                  <div class="p-8 rounded-2xl bg-slate-50 border border-slate-200 text-center text-slate-400">
                    <svg class="w-8 h-8 mx-auto text-slate-300 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138z" />
                    </svg>
                    <span>No certificate attachment uploaded for this institution.</span>
                  </div>
                }
              }
            </div>

            <!-- Reviewer Notes if already returned or rejected -->
            @if (inst.rejectionReason) {
              <div class="p-3.5 rounded-2xl bg-amber-50 border border-amber-300 text-amber-950 space-y-1">
                <span class="font-bold text-[10px] uppercase text-amber-800 tracking-wide flex items-center gap-1.5">
                  <svg class="w-4 h-4 text-amber-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span>Current Regulator Remarks / Instructions:</span>
                </span>
                <p class="whitespace-pre-wrap text-xs pl-5">{{ inst.rejectionReason }}</p>
              </div>
            }

            <!-- Regulator Decision Actions Bar inside Modal -->
            @if (authService.canManageOnboarding()) {
              <div class="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-3">
                <div class="flex items-center justify-between">
                  <span class="font-bold text-xs uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
                    <svg class="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    <span>Super Admin Registration Decision Actions</span>
                  </span>
                  <span
                    class="px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase font-mono tracking-wider"
                    [ngClass]="{
                      'bg-emerald-100 text-emerald-800 border border-emerald-300': inst.approvalStatus === 'APPROVED',
                      'bg-amber-100 text-amber-800 border border-amber-300': inst.approvalStatus === 'PENDING' || inst.onboardingStatus === 'CORRECTION',
                      'bg-rose-100 text-rose-800 border border-rose-300': inst.approvalStatus === 'REJECTED'
                    }"
                  >
                    Status: {{ inst.onboardingStatus === 'CORRECTION' ? 'RETURNED FOR CORRECTION' : inst.approvalStatus }}
                  </span>
                </div>
                <div class="flex flex-wrap items-center justify-end gap-2 pt-1 border-t border-slate-200">
                  <button
                    (click)="openDecisionModal(inst.uid, 'reject')"
                    class="px-3.5 py-2 rounded-xl bg-rose-600 hover:bg-rose-500 text-white font-bold text-xs inline-flex items-center gap-1.5 shadow-2xs transition cursor-pointer"
                    title="Reject Application"
                  >
                    <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span>Reject</span>
                  </button>

                  <button
                    (click)="openDecisionModal(inst.uid, 'correction')"
                    class="px-3.5 py-2 rounded-xl bg-amber-500 hover:bg-amber-400 text-white font-bold text-xs inline-flex items-center gap-1.5 shadow-2xs transition cursor-pointer"
                    title="Return application for correction"
                  >
                    <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <span>Return for Correction</span>
                  </button>

                  <button
                    (click)="onApproveInstitution(inst)"
                    class="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs inline-flex items-center gap-1.5 shadow-sm transition cursor-pointer"
                    title="Approve and activate institution onboarding"
                  >
                    <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Approve Institution</span>
                  </button>
                </div>
              </div>
            }

            <!-- Modal Action Buttons & Timestamps -->
            <div class="flex items-center justify-between pt-4 border-t border-slate-200 flex-wrap gap-3">
              <div class="text-[11px] text-slate-400 font-mono">
                <span>Onboarded: {{ inst.createdAt | date:'medium' }}</span>
              </div>
              <div class="flex items-center gap-2">
                <button
                  (click)="showViewModal.set(false)"
                  class="px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-xs cursor-pointer"
                >
                  Close
                </button>

                @if (authService.canManageOnboarding()) {
                  <!-- Reject Button -->
                  <button
                    (click)="openDecisionModal(inst.uid, 'reject')"
                    class="px-4 py-2 rounded-xl bg-rose-600 hover:bg-rose-500 text-white font-bold text-xs inline-flex items-center gap-1.5 shadow-2xs transition cursor-pointer"
                    title="Reject Application"
                  >
                    <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span>Reject</span>
                  </button>

                  <!-- Return for Correction Button -->
                  <button
                    (click)="openDecisionModal(inst.uid, 'correction')"
                    class="px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-400 text-white font-bold text-xs inline-flex items-center gap-1.5 shadow-2xs transition cursor-pointer"
                    title="Return application for correction"
                  >
                    <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <span>Return for Correction</span>
                  </button>

                  <!-- Approve Institution Button -->
                  <button
                    (click)="onApproveInstitution(inst)"
                    class="px-5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs inline-flex items-center gap-1.5 shadow-sm transition cursor-pointer"
                    title="Approve and activate institution onboarding"
                  >
                    <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Approve Institution</span>
                  </button>
                }
              </div>
            </div>
          </div>
        }
      </app-modal>

      <!-- SUPER ADMIN DECISION MODAL (REJECT / RETURN FOR CORRECTION) -->
      <app-modal
        [isOpen]="showDecisionModal()"
        [title]="decisionMode() === 'correction' ? 'Return Application for Correction' : 'Reject Institution Onboarding'"
        maxWidth="max-w-xl"
        zIndex="z-[60]"
        (close)="showDecisionModal.set(false)"
      >
        <div class="space-y-4 text-xs">
          <div class="p-3.5 rounded-xl" [ngClass]="decisionMode() === 'correction' ? 'bg-amber-50 border border-amber-300 text-amber-900' : 'bg-rose-50 border border-rose-300 text-rose-900'">
            @if (decisionMode() === 'correction') {
              <div class="flex items-center gap-2 font-bold text-xs uppercase tracking-wide text-amber-800 mb-1">
                <svg class="w-4 h-4 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <span>Action: Return Application for Correction</span>
              </div>
              <p>The applicant will be sent an automated notification with your specific remarks and a direct token link to edit and resubmit their application dossier.</p>
            } @else {
              <div class="flex items-center gap-2 font-bold text-xs uppercase tracking-wide text-rose-800 mb-1">
                <svg class="w-4 h-4 text-rose-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Action: Reject Institution Onboarding</span>
              </div>
              <p>Please state the official reason for rejecting this application. An automated notification will be sent to the institution contact person.</p>
            }
          </div>

          <div>
            <label class="block font-bold text-slate-700 uppercase mb-1">
              {{ decisionMode() === 'correction' ? 'Correction Remarks / Missing Documents *' : 'Rejection Reason *' }}
            </label>
            <textarea
              [(ngModel)]="decisionReason"
              rows="4"
              [placeholder]="decisionMode() === 'correction' ? 'e.g. Please upload an official application letter signed by the Accounting Officer, and verify the official vote number...' : 'e.g. Ineligible institutional sector classification or unverified registration credentials...'"
              class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-900 text-xs focus:outline-none focus:border-amber-500"
            ></textarea>
          </div>

          <div class="flex items-center justify-end gap-3 pt-3 border-t border-slate-200">
            <button
              (click)="showDecisionModal.set(false)"
              class="px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-xs cursor-pointer"
            >
              Cancel
            </button>
            <button
              (click)="onConfirmDecision()"
              [disabled]="isSubmittingDecision || !decisionReason.trim()"
              class="px-5 py-2 rounded-xl text-white font-bold text-xs shadow-sm disabled:opacity-50 inline-flex items-center gap-1.5 cursor-pointer"
              [ngClass]="decisionMode() === 'correction' ? 'bg-amber-600 hover:bg-amber-500' : 'bg-rose-600 hover:bg-rose-500'"
            >
              @if (isSubmittingDecision) {
                <svg class="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              }
              <span>{{ decisionMode() === 'correction' ? 'Send Return for Correction' : 'Confirm Rejection' }}</span>
            </button>
          </div>
        </div>
      </app-modal>

      <!-- EDIT INSTITUTION DETAILS MODAL -->
      <app-modal
        [isOpen]="showEditModal()"
        title="Edit Institution Details"
        [errorState]="modalError()"
        (close)="showEditModal.set(false)"
      >
        <form (ngSubmit)="onSaveInstitution()" class="space-y-4 text-xs">
          <!-- Institution Name -->
          <div>
            <label class="block font-bold text-slate-700 uppercase mb-1">Institution Legal Name *</label>
            <input
              type="text"
              [(ngModel)]="editForm.name"
              name="name"
              required
              class="w-full px-3 py-2 rounded-xl border border-slate-300 bg-white text-slate-900 text-xs focus:border-emerald-500"
            />
          </div>

          <!-- Email & Phone -->
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block font-bold text-slate-700 uppercase mb-1">Official Email *</label>
              <input
                type="email"
                [(ngModel)]="editForm.email"
                name="email"
                required
                class="w-full px-3 py-2 rounded-xl border border-slate-300 bg-white text-slate-900 text-xs focus:border-emerald-500"
              />
            </div>
            <div>
              <label class="block font-bold text-slate-700 uppercase mb-1">Official Phone *</label>
              <app-phone-input
                [(ngModel)]="editForm.phoneNumber"
                name="phoneNumber"
                [required]="true"
                placeholder="777 000 000"
              ></app-phone-input>
            </div>
          </div>

          <!-- Vote Number & Registration Number -->
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block font-bold text-slate-700 uppercase mb-1">Vote Number</label>
              <input
                type="text"
                [(ngModel)]="editForm.voteNumber"
                name="voteNumber"
                placeholder="e.g. VOTE-045"
                class="w-full px-3 py-2 rounded-xl border border-slate-300 bg-white text-slate-900 text-xs focus:border-emerald-500"
              />
            </div>
            <div>
              <label class="block font-bold text-slate-700 uppercase mb-1">Registration Number</label>
              <input
                type="text"
                [(ngModel)]="editForm.registrationNumber"
                name="registrationNumber"
                placeholder="e.g. REG-2024-998"
                class="w-full px-3 py-2 rounded-xl border border-slate-300 bg-white text-slate-900 text-xs focus:border-emerald-500"
              />
            </div>
          </div>

          <!-- Governance Settings -->
          <div class="grid grid-cols-2 gap-3 pt-1">
            <div>
              <label class="block font-bold text-slate-700 uppercase mb-1">Approval Status</label>
              <select
                [(ngModel)]="editForm.approvalStatus"
                name="approvalStatus"
                class="w-full px-3 py-2 rounded-xl border border-slate-300 bg-white text-slate-900 text-xs focus:border-emerald-500"
              >
                <option value="APPROVED">APPROVED</option>
                <option value="PENDING">PENDING</option>
                <option value="REJECTED">REJECTED</option>
              </select>
            </div>

            <div class="flex items-center pt-5 gap-2">
              <input
                type="checkbox"
                id="hasSsCheck"
                [(ngModel)]="editForm.hasSs"
                name="hasSs"
                class="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
              />
              <label for="hasSsCheck" class="font-bold text-slate-700 cursor-pointer">
                Security Server Connected
              </label>
            </div>
          </div>

          <!-- DPO Contact Section -->
          <div class="p-3.5 rounded-2xl bg-slate-50 border border-slate-200 space-y-3">
            <div class="text-[11px] font-bold text-emerald-800 uppercase tracking-wider">
              Data Protection Officer (DPO) Details
            </div>
            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block text-[11px] font-semibold text-slate-600 mb-1">First Name</label>
                <input
                  type="text"
                  [(ngModel)]="editForm.dpoFirstName"
                  name="dpoFirstName"
                  placeholder="DPO First Name"
                  class="w-full px-3 py-2 rounded-xl border border-slate-300 bg-white text-slate-900 text-xs"
                />
              </div>
              <div>
                <label class="block text-[11px] font-semibold text-slate-600 mb-1">Last Name</label>
                <input
                  type="text"
                  [(ngModel)]="editForm.dpoLastName"
                  name="dpoLastName"
                  placeholder="DPO Last Name"
                  class="w-full px-3 py-2 rounded-xl border border-slate-300 bg-white text-slate-900 text-xs"
                />
              </div>
            </div>
            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block text-[11px] font-semibold text-slate-600 mb-1">Email</label>
                <input
                  type="email"
                  [(ngModel)]="editForm.dpoEmail"
                  name="dpoEmail"
                  placeholder="dpo@institution.go.tz"
                  class="w-full px-3 py-2 rounded-xl border border-slate-300 bg-white text-slate-900 text-xs"
                />
              </div>
              <div>
                <label class="block text-[11px] font-semibold text-slate-600 mb-1">Phone</label>
                <app-phone-input
                  [(ngModel)]="editForm.dpoPhone"
                  name="dpoPhone"
                  [required]="false"
                  placeholder="777 000 000"
                ></app-phone-input>
              </div>
            </div>
          </div>

          <!-- Actions -->
          <div class="flex items-center justify-end gap-3 pt-3 border-t border-slate-200">
            <button
              type="button"
              (click)="showEditModal.set(false)"
              class="px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              [disabled]="saving()"
              class="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold transition shadow-md shadow-emerald-600/20 inline-flex items-center gap-2"
            >
              @if (saving()) {
                <svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Saving...
              } @else {
                Save Changes
              }
            </button>
          </div>
        </form>
      </app-modal>
    </div>
  `
})
export class InstitutionListComponent implements OnInit {
  private institutionService = inject(InstitutionService);
  private onboardingService = inject(OnboardingService);
  private toastService = inject(ToastService);
  public authService = inject(AuthService);

  institutions = signal<InstitutionResponse[]>([]);
  loading = signal<boolean>(false);
  saving = signal<boolean>(false);
  togglingUid = signal<string | null>(null);

  searchQuery = '';
  selectedStatusFilter = signal<'ALL' | 'ACTIVE' | 'INACTIVE'>('ALL');
  selectedApprovalFilter = signal<'ALL' | 'PENDING' | 'CORRECTION' | 'APPROVED' | 'REJECTED'>('ALL');

  // Modals state
  showViewModal = signal<boolean>(false);
  showEditModal = signal<boolean>(false);
  selectedInstitution = signal<InstitutionResponse | null>(null);
  modalError = signal<ModalErrorState | null>(null);

  // Super Admin Decision Modal State
  showDecisionModal = signal<boolean>(false);
  decisionMode = signal<'reject' | 'correction'>('correction');
  decisionReason = '';
  decisionUid = '';
  isSubmittingDecision = false;

  // Document Preview State
  private sanitizer = inject(DomSanitizer);
  parsedDocument = signal<ParsedDocument | null>(null);
  sanitizedDocumentUrl = signal<SafeResourceUrl | null>(null);
  parsedCertificate = signal<ParsedDocument | null>(null);
  sanitizedCertificateUrl = signal<SafeResourceUrl | null>(null);
  activeDocTab = signal<'letter' | 'certificate'>('letter');

  editForm: UpdateInstitutionRequest = {
    name: '',
    email: '',
    phoneNumber: '',
    voteNumber: '',
    registrationNumber: '',
    approvalStatus: 'APPROVED',
    hasSs: false,
    dpoFirstName: '',
    dpoLastName: '',
    dpoEmail: '',
    dpoPhone: ''
  };

  // KPIs
  activeCount = computed(() => this.institutions().filter(i => i.isActive).length);
  inactiveCount = computed(() => this.institutions().filter(i => !i.isActive).length);
  securityServerCount = computed(() => this.institutions().filter(i => i.hasSs).length);

  // Filtered institutions
  filteredInstitutions = computed(() => {
    let list = this.institutions();
    const filter = this.selectedStatusFilter();

    if (filter === 'ACTIVE') {
      list = list.filter(i => i.isActive);
    } else if (filter === 'INACTIVE') {
      list = list.filter(i => !i.isActive);
    }

    const appFilter = this.selectedApprovalFilter();
    if (appFilter !== 'ALL') {
      list = list.filter(i => {
        const s = (i.approvalStatus || '').toUpperCase();
        const onbS = (i.onboardingStatus || '').toUpperCase();
        if (appFilter === 'CORRECTION') {
          return onbS === 'CORRECTION' || s === 'CORRECTION';
        }
        return s === appFilter || onbS === appFilter;
      });
    }

    const query = this.searchQuery.trim().toLowerCase();
    if (!query) return list;

    return list.filter(i =>
      i.name.toLowerCase().includes(query) ||
      i.email.toLowerCase().includes(query) ||
      (i.registrationNumber && i.registrationNumber.toLowerCase().includes(query)) ||
      (i.voteNumber && i.voteNumber.toLowerCase().includes(query)) ||
      (i.dpoName && i.dpoName.toLowerCase().includes(query))
    );
  });

  ngOnInit() {
    this.loadInstitutions();
  }

  loadInstitutions() {
    this.loading.set(true);
    this.institutionService.getAllInstitutions().subscribe({
      next: (res) => {
        if (res.success && res.data) {
          this.institutions.set(res.data);
        }
        this.loading.set(false);
      },
      error: (err) => {
        this.loading.set(false);
        this.toastService.show(extractErrorDetails(err).message, 'error');
      }
    });
  }

  getInitials(name: string): string {
    if (!name) return 'IN';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }

  openViewModal(inst: InstitutionResponse) {
    this.selectedInstitution.set(inst);
    this.activeDocTab.set('letter');
    this.updateParsedDocuments(inst);
    this.showViewModal.set(true);

    // Fetch fresh details with complete document payloads
    this.institutionService.getInstitutionByUid(inst.uid).subscribe({
      next: (res) => {
        if (res.success && res.data) {
          this.selectedInstitution.set(res.data);
          this.updateParsedDocuments(res.data);
        }
      }
    });
  }

  private updateParsedDocuments(inst: InstitutionResponse) {
    const doc = this.parseDocument(inst.applicationLetter, 'application_letter');
    this.parsedDocument.set(doc);
    if (doc && doc.isPdf) {
      this.sanitizedDocumentUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(doc.dataUrl));
    } else {
      this.sanitizedDocumentUrl.set(null);
    }

    const cert = this.parseDocument(inst.certificate, 'compliance_certificate');
    this.parsedCertificate.set(cert);
    if (cert && cert.isPdf) {
      this.sanitizedCertificateUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(cert.dataUrl));
    } else {
      this.sanitizedCertificateUrl.set(null);
    }
  }

  parseDocument(docStr?: string, defaultPrefix = 'document'): ParsedDocument | null {
    if (!docStr || !docStr.trim()) return null;
    let content = '';
    let fileType = 'pdf';

    try {
      if (docStr.trim().startsWith('{')) {
        const parsed = JSON.parse(docStr);
        content = parsed.content || '';
        fileType = (parsed.fileType || 'pdf').toLowerCase().replace('.', '');
      } else {
        content = docStr;
      }
    } catch {
      content = docStr;
    }

    if (!content) return null;

    let mimeType = 'application/pdf';
    let isPdf = false;
    let isImage = false;

    if (fileType === 'pdf') {
      mimeType = 'application/pdf';
      isPdf = true;
    } else if (fileType === 'png') {
      mimeType = 'image/png';
      isImage = true;
    } else if (fileType === 'jpg' || fileType === 'jpeg') {
      mimeType = 'image/jpeg';
      isImage = true;
    } else if (fileType === 'doc' || fileType === 'docx') {
      mimeType = 'application/msword';
    }

    const dataUrl = content.startsWith('data:') ? content : `data:${mimeType};base64,${content}`;
    const fileName = `${defaultPrefix}.${fileType}`;

    return { content, fileType, dataUrl, fileName, isPdf, isImage };
  }

  downloadDocument(doc: ParsedDocument) {
    const link = document.createElement('a');
    link.href = doc.dataUrl;
    link.download = doc.fileName;
    link.click();
  }

  // Super Admin Decision Actions
  openDecisionModal(uid: string, mode: 'reject' | 'correction') {
    if (!this.authService.canManageOnboarding()) {
      this.toastService.error('Unauthorized: Registration decisions require onboarding management permissions.');
      return;
    }
    this.decisionUid = uid;
    this.decisionMode.set(mode);
    this.decisionReason = '';
    this.showDecisionModal.set(true);
  }

  onConfirmDecision() {
    if (!this.authService.canManageOnboarding()) {
      this.toastService.error('Unauthorized: Registration decisions require onboarding management permissions.');
      return;
    }
    if (!this.decisionReason.trim()) {
      this.toastService.warning('Please enter reason or remarks.');
      return;
    }

    this.isSubmittingDecision = true;
    const req = { reason: this.decisionReason.trim() };

    if (this.decisionMode() === 'correction') {
      this.onboardingService.returnForCorrection(this.decisionUid, req).subscribe({
        next: (res) => {
          this.isSubmittingDecision = false;
          this.showDecisionModal.set(false);
          this.toastService.success('Application returned for correction. Notification sent to institution.');
          this.loadInstitutions();
          if (this.selectedInstitution()?.uid === this.decisionUid) {
            this.selectedInstitution.update(curr => curr ? {
              ...curr,
              approvalStatus: 'PENDING',
              onboardingStatus: 'CORRECTION',
              rejectionReason: req.reason
            } : null);
          }
        },
        error: (err) => {
          this.isSubmittingDecision = false;
          this.toastService.error(err.error?.message || 'Failed to return application for correction.');
        }
      });
    } else {
      this.onboardingService.rejectOnboarding(this.decisionUid, req).subscribe({
        next: (res) => {
          this.isSubmittingDecision = false;
          this.showDecisionModal.set(false);
          this.toastService.success('Application rejected. Notification sent to applicant.');
          this.loadInstitutions();
          if (this.selectedInstitution()?.uid === this.decisionUid) {
            this.selectedInstitution.update(curr => curr ? {
              ...curr,
              approvalStatus: 'REJECTED',
              onboardingStatus: 'REJECTED',
              rejectionReason: req.reason,
              isActive: false
            } : null);
          }
        },
        error: (err) => {
          this.isSubmittingDecision = false;
          this.toastService.error(err.error?.message || 'Failed to reject application.');
        }
      });
    }
  }

  async onApproveInstitution(inst: InstitutionResponse) {
    if (!this.authService.canManageOnboarding()) {
      this.toastService.error('Unauthorized: Only authorized administrators can approve institutions.');
      return;
    }

    const confirmed = await this.toastService.confirm({
      title: 'Approve Institution Onboarding',
      message: `Are you sure you want to approve institution onboarding for "${inst.name}" and provision Zanzibar e-Gov Interoperability access?`,
      confirmText: 'Yes, Approve',
      cancelText: 'Cancel',
      confirmStyle: 'success'
    });
    if (!confirmed) return;

    this.onboardingService.approveOnboarding(inst.uid).subscribe({
      next: (res) => {
        if (res.success) {
          this.toastService.success(res.message || `Institution "${inst.name}" approved successfully!`);
          this.loadInstitutions();
          if (this.selectedInstitution()?.uid === inst.uid) {
            this.selectedInstitution.update(curr => curr ? {
              ...curr,
              approvalStatus: 'APPROVED',
              onboardingStatus: 'APPROVED',
              isActive: true,
              rejectionReason: undefined
            } : null);
          }
        }
      },
      error: (err) => {
        this.toastService.error(err.error?.message || 'Failed to approve institution.');
      }
    });
  }

  openEditModal(inst: InstitutionResponse) {
    this.selectedInstitution.set(inst);
    this.modalError.set(null);

    let dpoFirst = '';
    let dpoLast = '';
    if (inst.dpoName) {
      const parts = inst.dpoName.split(' ');
      dpoFirst = parts[0] || '';
      dpoLast = parts.slice(1).join(' ') || '';
    }

    this.editForm = {
      name: inst.name,
      email: inst.email,
      phoneNumber: inst.phoneNumber,
      voteNumber: inst.voteNumber || '',
      registrationNumber: inst.registrationNumber || '',
      approvalStatus: inst.approvalStatus,
      hasSs: inst.hasSs,
      dpoFirstName: dpoFirst,
      dpoLastName: dpoLast,
      dpoEmail: inst.dpoEmail || '',
      dpoPhone: inst.dpoPhone || ''
    };

    this.showEditModal.set(true);
  }

  onSaveInstitution() {
    const inst = this.selectedInstitution();
    if (!inst) return;

    this.saving.set(true);
    this.modalError.set(null);

    this.institutionService.updateInstitution(inst.uid, this.editForm).subscribe({
      next: (res) => {
        this.saving.set(false);
        if (res.success && res.data) {
          this.institutions.update(list =>
            list.map(i => i.uid === inst.uid ? res.data : i)
          );
          this.toastService.show(`Institution "${res.data.name}" updated successfully`, 'success');
          this.showEditModal.set(false);
        }
      },
      error: (err) => {
        this.saving.set(false);
        this.modalError.set(extractErrorDetails(err));
      }
    });
  }

  async toggleActiveStatus(inst: InstitutionResponse) {
    const newStatus = !inst.isActive;
    const actionLabel = newStatus ? 'activate' : 'deactivate';

    const confirmed = await this.toastService.confirm({
      title: `${newStatus ? 'Activate' : 'Deactivate'} Institution`,
      message: `Are you sure you want to ${actionLabel} institution "${inst.name}"?`,
      confirmText: newStatus ? 'Yes, Activate' : 'Yes, Deactivate',
      cancelText: 'Cancel',
      confirmStyle: newStatus ? 'success' : 'danger'
    });
    if (!confirmed) return;

    this.togglingUid.set(inst.uid);

    this.institutionService.toggleStatus(inst.uid, newStatus).subscribe({
      next: (res) => {
        this.togglingUid.set(null);
        if (res.success && res.data) {
          this.institutions.update(list =>
            list.map(i => i.uid === inst.uid ? res.data : i)
          );
          const stateLabel = newStatus ? 'activated' : 'deactivated';
          this.toastService.show(`Institution "${inst.name}" ${stateLabel} successfully`, 'success');
        }
      },
      error: (err) => {
        this.togglingUid.set(null);
        this.toastService.show(extractErrorDetails(err).message, 'error');
      }
    });
  }
}
